// Geocoding module using Mapbox API
// Provides simple interface for converting addresses to coordinates

export interface GeocodingResult {
  coordinates: [number, number]; // [longitude, latitude]
  placeName: string;
  confidence: number;
  bbox?: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
}

export interface GeocodingOptions {
  country?: string; // ISO 3166-1 alpha-2 country code (e.g., 'US')
  types?: string[]; // Filter by place type (e.g., ['address', 'poi'])
  limit?: number; // Number of results to return (default: 5)
  language?: string; // Language for results (e.g., 'en')
}

export interface ReverseGeocodingResult {
  coordinates: [number, number]; // [longitude, latitude]
  placeName: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
  };
}

class GeocodingService {
  private mapboxToken: string;
  private baseUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

  constructor() {
    // Get token from environment variable (embedded at build time)
    this.mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;
    
    if (!this.mapboxToken) {
      console.warn('VITE_MAPBOX_TOKEN is not set. Geocoding functionality will not work. Make sure to set mapbox_access_token in terraform.tfvars.');
    }
  }

  /**
   * Convert an address to coordinates
   * @param address - The address to geocode
   * @param options - Optional geocoding parameters
   * @returns Promise<GeocodingResult[]> - Array of geocoding results
   */
  async geocodeAddress(address: string, options: GeocodingOptions = {}): Promise<GeocodingResult[]> {
    if (!address.trim()) {
      throw new Error('Address cannot be empty');
    }

    if (!this.mapboxToken) {
      throw new Error('Mapbox access token is required');
    }

    const params = new URLSearchParams({
      access_token: this.mapboxToken,
      q: address.trim(),
      limit: (options.limit || 5).toString(),
      ...(options.country && { country: options.country }),
      ...(options.types && { types: options.types.join(',') }),
      ...(options.language && { language: options.language })
    });

    try {
      const response = await fetch(`${this.baseUrl}/${encodeURIComponent(address.trim())}.json?${params}`);
      
      if (!response.ok) {
        throw new Error(`Geocoding request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.features || !Array.isArray(data.features)) {
        return [];
      }

      return data.features.map((feature: any) => ({
        coordinates: feature.center as [number, number],
        placeName: feature.place_name,
        confidence: feature.relevance,
        bbox: feature.bbox as [number, number, number, number]
      }));
    } catch (error) {
      console.error('Geocoding error:', error);
      throw new Error(`Failed to geocode address: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Convert coordinates to an address (reverse geocoding)
   * @param coordinates - [longitude, latitude]
   * @param options - Optional reverse geocoding parameters
   * @returns Promise<ReverseGeocodingResult[]> - Array of reverse geocoding results
   */
  async reverseGeocode(coordinates: [number, number], options: { limit?: number; language?: string } = {}): Promise<ReverseGeocodingResult[]> {
    if (!this.isValidCoordinates(coordinates)) {
      throw new Error('Invalid coordinates provided');
    }

    if (!this.mapboxToken) {
      throw new Error('Mapbox access token is required');
    }

    const [longitude, latitude] = coordinates;
    const params = new URLSearchParams({
      access_token: this.mapboxToken,
      limit: (options.limit || 5).toString(),
      ...(options.language && { language: options.language })
    });

    try {
      const response = await fetch(`${this.baseUrl}/${longitude},${latitude}.json?${params}`);
      
      if (!response.ok) {
        throw new Error(`Reverse geocoding request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.features || !Array.isArray(data.features)) {
        return [];
      }

      return data.features.map((feature: any) => ({
        coordinates: [longitude, latitude],
        placeName: feature.place_name,
        address: this.extractAddressComponents(feature.context || [])
      }));
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      throw new Error(`Failed to reverse geocode coordinates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get the best match for an address (highest confidence result)
   * @param address - The address to geocode
   * @param options - Optional geocoding parameters
   * @returns Promise<GeocodingResult | null> - Best match or null if no results
   */
  async getBestMatch(address: string, options: GeocodingOptions = {}): Promise<GeocodingResult | null> {
    const results = await this.geocodeAddress(address, { ...options, limit: 1 });
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Validate if coordinates are within valid ranges
   * @param coordinates - [longitude, latitude]
   * @returns boolean - True if coordinates are valid
   */
  private isValidCoordinates(coordinates: [number, number]): boolean {
    const [longitude, latitude] = coordinates;
    return (
      typeof longitude === 'number' &&
      typeof latitude === 'number' &&
      longitude >= -180 && longitude <= 180 &&
      latitude >= -90 && latitude <= 90
    );
  }

  /**
   * Extract address components from Mapbox context array
   * @param context - Mapbox context array
   * @returns Object with address components
   */
  private extractAddressComponents(context: any[]): {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
  } {
    const address: any = {};

    context.forEach((item: any) => {
      const [id, text] = item.id.split('.');
      switch (id) {
        case 'address':
          address.street = text;
          break;
        case 'place':
          address.city = text;
          break;
        case 'region':
          address.state = text;
          break;
        case 'country':
          address.country = text;
          break;
        case 'postcode':
          address.postcode = text;
          break;
      }
    });

    return address;
  }

  /**
   * Get the current Mapbox token
   * @returns string - The current Mapbox token
   */
  getToken(): string {
    return this.mapboxToken;
  }

  /**
   * Set a new Mapbox token
   * @param token - The new Mapbox access token
   */
  setToken(token: string): void {
    this.mapboxToken = token;
  }
}

// Create and export a singleton instance
export const geocodingService = new GeocodingService();

// Export convenience functions for common use cases
export const geocodeAddress = (address: string, options?: GeocodingOptions) => 
  geocodingService.geocodeAddress(address, options);

export const reverseGeocode = (coordinates: [number, number], options?: { limit?: number; language?: string }) => 
  geocodingService.reverseGeocode(coordinates, options);

export const getBestMatch = (address: string, options?: GeocodingOptions) => 
  geocodingService.getBestMatch(address, options);

// Export the service class for advanced usage
export { GeocodingService }; 